import * as React from "react"
import type { DayButtonProps } from "@daypicker/react"
import { DayButton as DefaultDayButton } from "@daypicker/react"
import type { Product } from "../types"
import { Icon } from "./Icon"

type HirePriceContext = { start?: Date; product: Product | null; quantity: number; showPrices: boolean }
const HirePrices = React.createContext<HirePriceContext>({ product: null, quantity: 1, showPrices: false })

export function CutoffDayButton(props: DayButtonProps) {
    const { start, product, quantity, showPrices } = React.useContext(HirePrices)
    const cutoff = !props.modifiers.ahRangeStart && Boolean(props.modifiers.ahPast || props.modifiers.ahNoticeCutoff)
    const choice = showPrices && start && props.day.date > start
        && !props.modifiers.ahPriceUnavailable && !props.modifiers.outside
        ? inferRangeChoice({ from: start, to: props.day.date }, product) : null
    const total = choice ? money(choice.pricePence * quantity) : null
    const price = total?.replace(/\.00$/, "")
    const [pounds, pence] = price?.split(".") ?? []
    const priceUnits = Math.max(0, (pounds?.length ?? 0) - .25) + (pence ? (pence.length + 1) * .75 : 0)
    return <DefaultDayButton {...props} aria-disabled={cutoff || props.modifiers.ahRequiredHireDay || props["aria-disabled"]}
        title={total ? `${total} total hire${choice?.weeklyCapApplied ? " — capped at the weekly price" : ""}` : undefined}
        aria-label={total ? `${props["aria-label"]}, ${total} total hire${choice?.weeklyCapApplied ? ", capped at the weekly price" : ""}` : props["aria-label"]}>
        <span className="ah-calendar-day-number">{props.children}</span>
        {price && <small aria-hidden="true" className={`ah-calendar-day-price${choice?.weeklyCapApplied ? " is-capped" : ""}`}
            title={choice?.weeklyCapApplied ? "Our price promise: capped at the weekly price" : undefined}
            style={{ "--ah-price-units": priceUnits } as React.CSSProperties}>
            {choice?.weeklyCapApplied && <Icon name="zap" />}<span><span className="ah-calendar-price-currency">£</span>{pounds?.slice(1)}{pence && <span className="ah-calendar-price-pence">.{pence}</span>}</span>
        </small>}
    </DefaultDayButton>
}

import type { Matcher } from "@daypicker/react"
import { DayPicker } from "@daypicker/react"
import { enGB } from "@daypicker/react/locale"
import { dayDifference, inferRangeChoice, isRequiredHireDay } from "../dateSelection"
import { money, toDateOnly } from "../format"
import type { CheckoutController } from "../useCheckoutController"

type CalendarProps = Pick<CheckoutController,
    "lastMonth" | "unavailableDates" | "unavailableServiceDates" | "activeDateField" | "range" | "product" | "quantity" | "calendarSnapshot" | "today" | "isSelectedStartDate" | "calendarReady" | "calendarRefreshing" | "chooseDay" | "firstMonth" | "isMobile">

export function BookingCalendar(inputs: CalendarProps) {
    const {
        lastMonth,
        unavailableDates,
        unavailableServiceDates,
        activeDateField,
        range,
        product,
        quantity,
        calendarSnapshot,
        today,
        isSelectedStartDate,
        calendarReady,
        calendarRefreshing,
        chooseDay,
        firstMonth,
        isMobile,
    } = inputs
    const hoverQuery = "(min-width: 700px) and (hover: hover) and (pointer: fine)"
    const [canHover, setCanHover] = React.useState(false)
    const [hoveredDay, setHoveredDay] = React.useState<Date>()
    React.useEffect(() => {
        const media = window.matchMedia(hoverQuery)
        const update = () => { setCanHover(media.matches); setHoveredDay(undefined) }
        update()
        media.addEventListener("change", update)
        return () => media.removeEventListener("change", update)
    }, [])
    React.useEffect(() => { setHoveredDay(undefined) }, [range?.from, range?.to, activeDateField, product?.slug, quantity, calendarSnapshot, calendarReady, isMobile])
    const showPrices = Boolean(range?.from)
    const hirePrices = React.useMemo(() => ({ start: range?.from, product, quantity, showPrices }), [range?.from, product, quantity, showPrices])
    // Price each possible collection date even while the delivery field is being edited.
    const unavailableCollectionDates = React.useMemo(() => new Set([
        ...unavailableDates.map(toDateOnly), ...(calendarSnapshot?.collectionUnavailableDates ?? []),
    ]), [unavailableDates, calendarSnapshot])
    const isUnavailableDate = (date: Date) => date > lastMonth
        || [...unavailableDates, ...unavailableServiceDates].some((blocked) => dayDifference(blocked, date) === 0)
        || Boolean(activeDateField === "end" && range?.from
            && (date < range.from || !inferRangeChoice({ from: range.from, to: date }, product)))
    const isNoticeDate = (date: Date) => activeDateField === "start"
        && Boolean(calendarSnapshot?.deliveryNoticeUnavailableDates.includes(toDateOnly(date)))
    const isCutoffDate = (date: Date) => date < today || isNoticeDate(date)
    const previewEnd = canHover && !isMobile && activeDateField === "end" && showPrices && calendarReady && !calendarRefreshing
        && hoveredDay && range?.from && hoveredDay > range.from && !isCutoffDate(hoveredDay) && !isUnavailableDate(hoveredDay)
        ? hoveredDay : undefined
    // Preview only the visual modifiers; actual selection and aria-selected remain unchanged.
    const displayRange = previewEnd ? { from: range!.from!, to: previewEnd } : range
    const isRequiredDay = (date: Date) => isRequiredHireDay(date, range?.from, product)
    // Keep the required middle day interactive so it can explain the minimum hire.
    const disabledDates: Matcher[] = [(date: Date) => !isSelectedStartDate(date)
        && (!calendarReady || (isUnavailableDate(date) && !(activeDateField === "end" && isRequiredDay(date))))]
    return (
        <HirePrices.Provider value={hirePrices}>
        <DayPicker
            className={`ah-booking-calendar${showPrices ? " has-hire-prices" : ""}${previewEnd ? " is-previewing" : ""}`}
            mode="range"
            aria-busy={calendarRefreshing}
            selected={range}
            onSelect={() => { }}
            onDayClick={(day, modifiers) => { setHoveredDay(undefined); chooseDay(day, modifiers) }}
            onDayMouseEnter={(day, modifiers) => {
                setHoveredDay(canHover && !isMobile && !modifiers.disabled && !modifiers.outside ? day : undefined)
            }}
            onDayMouseLeave={() => setHoveredDay(undefined)}
            onMonthChange={() => setHoveredDay(undefined)}
            modifiers={{
                ahPriceUnavailable: (date: Date) => !calendarReady || date < today || date > lastMonth || unavailableCollectionDates.has(toDateOnly(date)),
                ahRequiredHireDay: (date: Date) => activeDateField === "end" && isRequiredDay(date),
                ahSelected: (date: Date) => isRequiredDay(date) || Boolean(displayRange?.from && dayDifference(displayRange.from, date) >= 0
                    && dayDifference(date, displayRange.to ?? displayRange.from) >= 0),
                ahRangeStart: displayRange?.from,
                ahRangeEnd: displayRange?.to,
                ahRangeMiddle: (date: Date) => isRequiredDay(date) || Boolean(displayRange?.from && displayRange.to
                    && dayDifference(displayRange.from, date) > 0 && dayDifference(date, displayRange.to) > 0),
                ahAvailable: (date: Date) => (calendarReady || calendarRefreshing) && !isCutoffDate(date) && !isUnavailableDate(date),
                ahUnavailable: (date: Date) => date >= today && (calendarReady || calendarRefreshing) && (isCutoffDate(date) || isUnavailableDate(date)),
                ahRefreshing: (date: Date) => calendarRefreshing && !isUnavailableDate(date),
                ahPast: { before: today },
                ahNoticeCutoff: isNoticeDate,
            }}
            modifiersClassNames={{ ahRefreshing: "ah-calendar-refreshing", ahAvailable: "ah-calendar-available", ahSelected: "ah-calendar-selected", ahRangeStart: "ah-calendar-range-start", ahRangeEnd: "ah-calendar-range-end", ahRangeMiddle: "ah-calendar-range-middle", ahUnavailable: "ah-calendar-unavailable", ahPast: "ah-calendar-cutoff", ahNoticeCutoff: "ah-calendar-cutoff" }}
            disabled={disabledDates}
            startMonth={firstMonth}
            defaultMonth={firstMonth}
            endMonth={lastMonth}
            numberOfMonths={isMobile ? (lastMonth.getFullYear() - firstMonth.getFullYear()) * 12 + lastMonth.getMonth() - firstMonth.getMonth() + 1 : 2}
            locale={enGB}
            weekStartsOn={1}
            showOutsideDays={false}
            hideNavigation={isMobile}
            navLayout={isMobile ? undefined : "around"}
            formatters={{ formatWeekdayName: date => new Intl.DateTimeFormat("en-GB", { weekday: "narrow" }).format(date) }}
            aria-label="Choose delivery and collection dates"
            components={{ DayButton: CutoffDayButton }}
        />
        </HirePrices.Provider>
    )
}
